import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Container } from "reactstrap";
import "react-toastify/dist/ReactToastify.css";
import { Search } from "lucide-react";
import { toast } from "react-toastify";

import {
  FetchDeleteRequests,
  CancelDeleteRequest,
  AddDeleteRequestConclusion,
  FinalizeDeleteRequest,
} from "../../apis/User";
import Paginate from "../../components/shared/paginate";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "../../components/shared/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/shared/alertDialog";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

const reasonLabels = {
  safety: "Safety / privacy",
  hosting: "Cannot host anymore",
  support: "Cannot contact support",
  other: "Other",
};

const DeleteRequests = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.MANAGE_USERS);

  const [requests, setRequests] = useState([]);
  const [currentPage, setCurrentPage] = useState("1");
  const [totalPages, setTotalPages] = useState("1");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [conclusionNote, setConclusionNote] = useState("");
  const [noteError, setNoteError] = useState(false);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const limit = "10";

  const columns = useMemo(
    () => [
      "User",
      "Phone",
      "Email",
      "Reason",
      "Requested",
      "Calls",
      "Actions",
    ],
    []
  );

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await FetchDeleteRequests(currentPage, limit, keyword);
      setRequests(response?.requests || []);
      setTotalPages(String(response?.totalPages || 1));
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to load delete requests"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage("1");
    const timer = setTimeout(() => {
      fetchRequests();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  const openCallDialog = (request) => {
    setSelectedRequest(request);
    setConclusionNote("");
    setNoteError(false);
    setCallDialogOpen(true);
  };

  const openHistoryDialog = (request) => {
    setSelectedRequest(request);
    setHistoryDialogOpen(true);
  };

  const openDisableDialog = (request) => {
    setSelectedRequest(request);
    setDisableDialogOpen(true);
  };

  const handleCancelRequest = async () => {
    if (!selectedRequest) return;
    if (!conclusionNote.trim()) {
      setNoteError(true);
      return;
    }
    setSubmitting(true);
    try {
      await CancelDeleteRequest(selectedRequest._id, conclusionNote.trim());
      toast.success("Delete request cancelled — user is keeping the account");
      setCallDialogOpen(false);
      fetchRequests();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to cancel delete request"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleInsistedConclusion = async () => {
    if (!selectedRequest) return;
    if (!conclusionNote.trim()) {
      setNoteError(true);
      return;
    }
    setSubmitting(true);
    try {
      const response = await AddDeleteRequestConclusion(
        selectedRequest._id,
        conclusionNote.trim()
      );
      const count = response?.data?.conclusionsCount;
      const required = response?.data?.requiredConclusions || 3;
      toast.success(
        count
          ? `Conclusion saved (${count}/${required}). Call again if needed.`
          : "Conclusion saved"
      );
      setCallDialogOpen(false);
      fetchRequests();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to save call conclusion"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinalize = async () => {
    if (!selectedRequest) return;
    setSubmitting(true);
    try {
      await FinalizeDeleteRequest(selectedRequest._id);
      toast.success("Account disabled");
      setDisableDialogOpen(false);
      fetchRequests();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to disable account"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatReason = (request) => {
    const label = reasonLabels[request.reason] || request.reason || "-";
    if (request.reason === "other" && request.otherReason) {
      return `${label}: ${request.otherReason}`;
    }
    return label;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]" fluid>
          <div className="p-6 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl dark:text-[#c7d8eb]">
                Account delete requests
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Call the user manually, record each call conclusion, and only
                disable the account after 3 insisted conclusions.
              </p>
            </div>

            <div className="lg:flex flex-col lg:border lg:border-b-0 border-gray-400 dark:border-white">
              <div className="border-x border-t lg:border-none border-gray-500 dark:border-white rounded-[6px_6px_0px_0px] w-full flex flex-col lg:flex-row justify-between gap-4 p-4">
                <div className="relative border max-w-[400px] border-gray-500 dark:border-white rounded-md w-full">
                  <Search
                    size={16}
                    className="absolute left-2 bottom-1/2 translate-y-1/2"
                  />
                  <input
                    className="pl-8 pr-6 py-2 text-base rounded-md w-full dark:bg-darkBg2"
                    type="search"
                    placeholder="Search name, email, phone"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <div className="hidden lg:flex">
                  {columns.map((column, index) => (
                    <div
                      key={`column-${index}`}
                      className="w-[14.28%] min-w-[140px] content-center border-y border-gray-500 dark:border-white break-words py-2 text-center font-semibold"
                    >
                      {column}
                    </div>
                  ))}
                </div>

                {loading && (
                  <div className="p-6 text-center text-gray-500">Loading…</div>
                )}

                {!loading && requests.length === 0 && (
                  <div className="p-6 text-center text-gray-500">
                    No pending delete requests.
                  </div>
                )}

                {!loading &&
                  requests.map((request) => (
                    <React.Fragment key={request._id}>
                      <div className="hidden lg:flex">
                        <div className="w-[14.28%] min-w-[140px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          <div className="font-medium">
                            {request.firstName} {request.lastName}
                          </div>
                          <Link
                            className="text-secondaryColor text-xs"
                            to={`/profile-view/${request._id}`}
                          >
                            View profile
                          </Link>
                        </div>
                        <div className="w-[14.28%] min-w-[140px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {request.mobileNumber || "-"}
                        </div>
                        <div className="w-[14.28%] min-w-[140px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {request.email}
                        </div>
                        <div className="w-[14.28%] min-w-[140px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-sm">
                          {formatReason(request)}
                        </div>
                        <div className="w-[14.28%] min-w-[140px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {request.requestedAt
                            ? String(request.requestedAt).slice(0, 10)
                            : "-"}
                        </div>
                        <div className="w-[14.28%] min-w-[140px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          <button
                            type="button"
                            className="text-secondaryColor"
                            onClick={() => openHistoryDialog(request)}
                          >
                            {request.conclusionsCount}/
                            {request.requiredConclusions}
                          </button>
                        </div>
                        <div className="w-[14.28%] min-w-[140px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {canManage ? (
                            <div className="flex flex-col gap-1 items-center">
                              <button
                                type="button"
                                className="text-secondaryColor"
                                onClick={() => openCallDialog(request)}
                              >
                                Record call
                              </button>
                              {request.canDisable && (
                                <button
                                  type="button"
                                  className="text-red-600 font-semibold"
                                  onClick={() => openDisableDialog(request)}
                                >
                                  Disable account
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </div>

                      <div className="block lg:hidden mb-6 border border-gray-500 dark:border-white">
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">User</div>
                          <div>
                            {request.firstName} {request.lastName}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Phone</div>
                          <div>{request.mobileNumber || "-"}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Email</div>
                          <div>{request.email}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Reason</div>
                          <div className="text-right text-sm">
                            {formatReason(request)}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Calls</div>
                          <button
                            type="button"
                            className="text-secondaryColor"
                            onClick={() => openHistoryDialog(request)}
                          >
                            {request.conclusionsCount}/
                            {request.requiredConclusions}
                          </button>
                        </div>
                        {canManage && (
                          <div className="flex flex-col gap-2 p-2">
                            <button
                              type="button"
                              className="text-secondaryColor"
                              onClick={() => openCallDialog(request)}
                            >
                              Record call
                            </button>
                            {request.canDisable && (
                              <button
                                type="button"
                                className="text-red-600 font-semibold"
                                onClick={() => openDisableDialog(request)}
                              >
                                Disable account
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  ))}
              </div>
            </div>

            <Paginate
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
            />
          </div>
        </Container>
      </div>

      <Dialog open={callDialogOpen} onOpenChange={setCallDialogOpen}>
        <DialogContent className="max-w-lg select-none bg-gray-200 dark:bg-[#171d37] dark:text-[#c7d8eb]">
          <DialogHeader>
            <h2 className="text-lg font-semibold">Record call conclusion</h2>
          </DialogHeader>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Call{" "}
            <strong>
              {selectedRequest?.firstName} {selectedRequest?.lastName}
            </strong>{" "}
            at <strong>{selectedRequest?.mobileNumber || "—"}</strong>{" "}
            manually, then save the outcome.
          </p>
          <textarea
            className="mt-3 w-full min-h-[120px] rounded-md border border-gray-400 p-3 bg-white dark:bg-darkBg2 dark:border-white"
            placeholder="Call conclusion notes…"
            value={conclusionNote}
            onChange={(e) => {
              setConclusionNote(e.target.value);
              setNoteError(false);
            }}
          />
          {noteError && (
            <p className="text-sm text-red-500">
              Please enter the call conclusion.
            </p>
          )}
          <DialogFooter className="mt-4 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              disabled={submitting}
              onClick={handleCancelRequest}
              className="rounded-md border border-green-600 px-4 py-2 text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50"
            >
              User keeping account
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={handleInsistedConclusion}
              className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              User insisted
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-lg select-none bg-gray-200 dark:bg-[#171d37] dark:text-[#c7d8eb]">
          <DialogHeader>
            <h2 className="text-lg font-semibold">Call conclusions</h2>
          </DialogHeader>
          <div className="mt-2 flex flex-col gap-3 max-h-[360px] overflow-y-auto">
            {(selectedRequest?.conclusions || []).length === 0 && (
              <p className="text-sm text-gray-500">No conclusions yet.</p>
            )}
            {(selectedRequest?.conclusions || []).map((item, index) => (
              <div
                key={`${selectedRequest?._id}-conclusion-${index}`}
                className="rounded-md border border-gray-300 bg-white p-3 dark:border-white/40 dark:bg-darkBg2"
              >
                <div className="text-xs text-gray-500 mb-1">
                  Call #{index + 1}
                  {item.createdAt
                    ? ` · ${String(item.createdAt).slice(0, 19).replace("T", " ")}`
                    : ""}
                  {item.admin
                    ? ` · ${item.admin.firstName || ""} ${item.admin.lastName || ""}`.trim()
                    : ""}
                </div>
                <p className="text-sm whitespace-pre-wrap">{item.note}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <AlertDialogContent className="select-none bg-gray-200 dark:bg-[#171d37] dark:text-[#c7d8eb]">
          <AlertDialogHeader>
            <AlertDialogTitle>Disable this account?</AlertDialogTitle>
            <AlertDialogDescription>
              This soft-disables{" "}
              <strong>
                {selectedRequest?.firstName} {selectedRequest?.lastName}
              </strong>{" "}
              after {selectedRequest?.requiredConclusions || 3} call
              conclusions. The account will no longer be able to log in, and the
              delete request will be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={submitting} onClick={handleFinalize}>
              Disable account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </React.Fragment>
  );
};

export default DeleteRequests;
